import React, { useEffect, useState, useMemo } from "react";
import {
  Row,
  Container,
  Col,
  Button,
  Badge,
  ListGroup,
  Card,
  Spinner,
} from "react-bootstrap";
import useOrderApi, { Order, OrderStatus, OrderStatusEnum } from "../../managers/OrderManager";
import { useParams } from "react-router-dom";
import Loading, { LoadingStates } from "../../components/Loading";
import { formatCurrency } from "../../utilities/formatCurrency";
import { useAuth0 } from "@auth0/auth0-react";
import UserAccess from "../../auth/UserAccess";
import useUserApi, { User, UserRoles } from "../../managers/UserManager";
import { toast } from "react-toastify";
import { formatClientLocalDate } from "../../utilities/DateUtil";

export const statusToColor = (status?: OrderStatus): string => {
  const s = status ?? OrderStatusEnum.Draft;
  switch (s) {
    case OrderStatusEnum.Submitted:
    case OrderStatusEnum.Pending:
    case OrderStatusEnum.Processing:
    case OrderStatusEnum.Shipped:
      return "success";
    case OrderStatusEnum.Delivered:
      return "primary";
    case OrderStatusEnum.Canceled:
    case OrderStatusEnum.Refunded:
      return "secondary";
    case OrderStatusEnum.Failed:
      return "warning";
    case OrderStatusEnum.Draft:
    default:
      return "secondary";
  }
};

const safeDate = (d?: string) => (d ? new Date(d) : null);

const getPreviewUrl = (item: any): string | undefined => {
  // Try common shapes: array of urls, single url, imageUrl field
  const meta = item?.metadata;
  if (Array.isArray(meta?.previewImageUrls) && meta.previewImageUrls.length > 0) {
    return meta.previewImageUrls[0];
  }
  if (typeof meta?.previewImageUrls === "string") {
    return meta.previewImageUrls;
  }
  return item?.imageUrl || meta?.imageUrl;
};

const itemUnitPrice = (item: any): number =>
  Number(item?.price ?? item?.unitPrice ?? item?.amount ?? 0);
const itemLineTotal = (item: any): number => itemUnitPrice(item) * Number(item?.quantity ?? 0);

const OrderDetail: React.FC = () => {
  const { user, isLoading } = useAuth0();
  const { getCurrentUser } = useUserApi();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const { getOrder, submitOrder, updateOrderTracking } = useOrderApi();
  const { orderId = "" } = useParams();
  const [loadingState, setLoadingState] = useState<
    typeof LoadingStates[keyof typeof LoadingStates]
  >(LoadingStates.LOADING);
  const [orderDetails, setOrderDetails] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [updatingTracking, setUpdatingTracking] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    (async () => {
      try {
        const orderData: Order = await getOrder(orderId, user?.sub || "0");
        setOrderDetails(orderData);
        setLoadingState(LoadingStates.SUCCESS);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Failed to retrieve order details.");
        setLoadingState(LoadingStates.ERROR);
      }
    })();
  }, [orderId, user, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      getCurrentUser()
        .then(setCurrentUser)
        .catch((err) => {
          console.error("Failed to fetch current user:", err);
        });
    }
  }, [isLoading]);

  const totals = useMemo(() => {
    const o: Order = orderDetails || {};
    const subtotal =
      typeof o.amountSubtotal === "number"
        ? o.amountSubtotal
        : Array.isArray(o.lineItems)
          ? o.lineItems.reduce((sum: number, it: any) => sum + itemLineTotal(it), 0)
          : 0;

    return {
      subtotal,
      shipping: Number(o.amountShipping ?? 0),
      tax: Number(o.amountTax ?? 0),
      discount: Number(o.amountDiscount ?? 0),
      total: Number(o.amountTotal ?? subtotal + Number(o.amountShipping ?? 0) + Number(o.amountTax ?? 0) - Number(o.amountDiscount ?? 0)),
      currency: o.currency || "USD",
    };
  }, [orderDetails]);

  if (loadingState === LoadingStates.LOADING) {
    return <Loading message="Loading order details..." />;
  }

  if (error || loadingState === LoadingStates.ERROR) {
    return <Loading state={LoadingStates.ERROR} message={error || "Something went wrong."} />;
  }
  if (!orderDetails) {
    return <div className="p-4">No order details found.</div>;
  }

  const submittedDate = orderDetails.createdDate || orderDetails.updatedDate;
  const statusColor = statusToColor(orderDetails.status);

  const handleResubmit = async (order: Order) => {
    if (resubmitting) return;
    setResubmitting(true);
    try {
      const updated = await submitOrder(order);
      setOrderDetails(updated);
      toast.success(`Order ${updated.customerOrderId || updated.orderId} resubmitted successfully.`);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to resubmit order.";
      console.error("Error resubmitting order:", err);
      toast.error(msg);
    } finally {
      setResubmitting(false);
    }
  };

  const handleUpdateTracking = async () => {
    if (updatingTracking || !orderDetails?.orderId) return;
    setUpdatingTracking(true);

    // Store current order details as fallback
    const originalOrder = orderDetails;

    try {
      console.log('[OrderDetail] Updating tracking for order:', orderDetails.orderId);
      const updated = await updateOrderTracking(orderDetails.orderId);
      console.log('[OrderDetail] Received updated order:', updated);
      console.log('[OrderDetail] Tracking count:', updated.tracking?.length || 0);
      console.log('[OrderDetail] Line items with tracking:', updated.lineItems?.filter((li: any) => li.tracking?.trackingNumber).length || 0);

      setOrderDetails(updated);
      toast.success("Tracking information updated successfully.");
    } catch (err: any) {
      // Preserve original order data on error
      setOrderDetails(originalOrder);
      const msg = err?.message || "Failed to update tracking information.";
      console.error("Error updating tracking:", err);
      toast.error(msg);
    } finally {
      setUpdatingTracking(false);
    }
  };

  return (
    <Container className="py-4">
      {/* Header */}
      <Row className="gy-3 align-items-start">
        <Col xs={12}>
          <h1 className="h4 mb-2">Order #{orderDetails.customerOrderId || orderDetails.orderId}</h1>
          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
            <div className="text-muted">
              {submittedDate
                ? `Placed on ${formatClientLocalDate(submittedDate)}`
                : "Placed date unavailable"}
            </div>
            <Badge bg={statusColor} className="text-uppercase">
              {orderDetails.status || "Unknown"}
            </Badge>
          </div>
        </Col>
      </Row>

      <Row className="gy-4 mt-1">
        {/* Items - Primary content first on mobile */}
        <Col xs={12} lg={8}>
          <Card className="shadow-sm">
            <Card.Header as="h5" className="h6 mb-0">Items in your order</Card.Header>
            <Card.Body className="p-0">
              <ListGroup className="list-group-flush">
                {orderDetails.lineItems?.map((item: any, idx: number) => {
                  const thumb = getPreviewUrl(item);
                  const name =
                    item?.name ||
                    item?.metadata?.title ||
                    item?.metadata?.name ||
                    item?.catalogItemId ||
                    "Item";
                  const variant =
                    item?.variant ||
                    item?.metadata?.variant ||
                    item?.catalogItemVariantId ||
                    undefined;
                  const qty = Number(item?.quantity ?? 0);
                  const unit = itemUnitPrice(item);
                  const lineTotal = itemLineTotal(item);

                  const tracking = item?.tracking;
                  const fulfillmentStatus = item?.fulfillmentStatus;

                  return (
                    <ListGroup.Item key={idx} className="d-flex gap-3 align-items-start">
                      {thumb && (
                        <img
                          src={thumb}
                          alt={`${name} preview`}
                          width={72}
                          height={72}
                          style={{ objectFit: "cover", borderRadius: 8 }}
                        />
                      )}
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{name}</div>
                        <div className="text-muted small">
                          {variant ? <>Variant: {variant} · </> : null}
                          Qty: {qty}
                        </div>
                        <div className="text-muted small">
                          Unit: {formatCurrency(unit)}
                        </div>

                        {/* Fulfillment Status Badge */}
                        {fulfillmentStatus && (
                          <div className="mt-2">
                            <Badge
                              bg={fulfillmentStatus === 'fulfilled' ? 'success' : 'secondary'}
                              className="me-2"
                            >
                              {fulfillmentStatus === 'fulfilled' ? 'Shipped' : 'Pending'}
                            </Badge>
                          </div>
                        )}

                        {/* Item Tracking Info */}
                        {tracking?.trackingNumber && (
                          <div className="mt-2">
                            <div className="small">
                              <span className="text-muted">Tracking: </span>
                              {tracking.trackingUrl ? (
                                <a
                                  href={tracking.trackingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none"
                                >
                                  {tracking.trackingNumber}
                                </a>
                              ) : (
                                <span>{tracking.trackingNumber}</span>
                              )}
                            </div>
                            {tracking.carrier && (
                              <div className="small text-muted">
                                Carrier: {tracking.carrier}
                              </div>
                            )}
                            {tracking.status && (
                              <div className="small text-muted text-capitalize">
                                Status: {tracking.status.replace('_', ' ')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="ms-auto fw-semibold">
                        {formatCurrency(lineTotal)}
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* Order Summary - shows below items on mobile; sticky on desktop for convenience */}
        <Col xs={12} lg={4}>
          <Card className="shadow-sm position-lg-sticky">
            <Card.Header as="h5" className="h6 mb-0">Order summary</Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Subtotal</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Shipping</span>
                <span>{formatCurrency(totals.shipping)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Tax</span>
                <span>{formatCurrency(totals.tax)}</span>
              </div>
              {totals.discount ? (
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Discount</span>
                  <span>-{formatCurrency(totals.discount)}</span>
                </div>
              ) : null}
              <hr />
              <div className="d-flex justify-content-between">
                <span className="fw-semibold">Total</span>
                <span className="fw-bold">{formatCurrency(totals.total)}</span>
              </div>

              {/* Tracking Actions */}
              <div className="d-flex flex-column gap-2 mt-3">
                {orderDetails.tracking && orderDetails.tracking.length > 0 && (
                  <div className="d-flex flex-wrap gap-2">
                    {orderDetails.tracking.map((track, index) => (
                      track.trackingUrl && (
                        <Button 
                          key={index}
                          as="a" 
                          href={track.trackingUrl} 
                          target="_blank"
                          variant="outline-primary" 
                          size="sm"
                        >
                          Track Package {orderDetails.tracking!.length > 1 ? `#${index + 1}` : ''}
                        </Button>
                      )
                    ))}
                  </div>
                )}
                
                {/* Update Tracking Button */}
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={handleUpdateTracking}
                  disabled={updatingTracking}
                >
                  {updatingTracking && <Spinner size="sm" className="me-2" />}
                  {updatingTracking ? "Updating..." : "Check for Tracking Updates"}
                </Button>
              </div>

              {/* Conditional Resubmit Button for Super Admins */}
              <UserAccess
                user={currentUser}
                roles={[UserRoles.SUPER_ADMIN]}
                operation="any"
              >
                <div className="d-flex mt-3">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleResubmit(orderDetails)}
                    disabled={resubmitting}
                  >
                    {resubmitting && <Spinner size="sm" className="me-2" />}
                    {resubmitting ? "Resubmitting..." : "Resubmit Order"}
                  </Button>
                </div>
              </UserAccess>
            </Card.Body>
          </Card>
        </Col>

        {/* Shipping Address & Contact */}
        <Col xs={12} lg={6}>
          <Card className="shadow-sm">
            <Card.Header as="h5" className="h6 mb-0">Shipping address</Card.Header>
            <Card.Body>
              <div>{orderDetails.shippingDetails?.name}</div>
              <div>{orderDetails.shippingDetails?.address?.address1}</div>
              {orderDetails.shippingDetails?.address?.address2 && (
                <div>{orderDetails.shippingDetails.address.address2}</div>
              )}
              <div>
                {orderDetails.shippingDetails?.address?.city}
                {orderDetails.shippingDetails?.address?.state
                  ? `, ${orderDetails.shippingDetails.address.state}`
                  : ""}
                {" "}
                {orderDetails.shippingDetails?.address?.postalCode}
              </div>
              <div>{orderDetails.shippingDetails?.address?.country}</div>
              <div className="mt-2">
                {orderDetails.shippingDetails?.email && (
                  <div>
                    <span className="text-muted">Email: </span>
                    <a href={`mailto:${orderDetails.shippingDetails.email}`}>
                      {orderDetails.shippingDetails.email}
                    </a>
                  </div>
                )}
                {orderDetails.shippingDetails?.phone && (
                  <div>
                    <span className="text-muted">Phone: </span>
                    <a href={`tel:${orderDetails.shippingDetails.phone}`}>
                      {orderDetails.shippingDetails.phone}
                    </a>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Order Info (IDs, currency, optional payment/delivery details) */}
        <Col xs={12} lg={6}>
          <Card className="shadow-sm">
            <Card.Header as="h5" className="h6 mb-0">Order info</Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Order ID</span>
                <span>{orderDetails.customerOrderId || orderDetails.orderId}</span>
              </div>
              {orderDetails.currency && (
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Currency</span>
                  <span>{orderDetails.currency}</span>
                </div>
              )}
              {orderDetails.shippingDetails?.method && (
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Shipping Method</span>
                  <span className="text-capitalize">{orderDetails.shippingDetails.method}</span>
                </div>
              )}
              
              {/* Display tracking information */}
              {orderDetails.tracking && orderDetails.tracking.length > 0 && (
                <>
                  {orderDetails.tracking.map((track, index) => (
                    <div key={index}>
                      {track.trackingNumber && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            Tracking #{orderDetails.tracking!.length > 1 ? `${index + 1}` : ''}
                          </span>
                          <span>{track.trackingNumber}</span>
                        </div>
                      )}
                      {track.carrier && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            Carrier {orderDetails.tracking!.length > 1 ? `#${index + 1}` : ''}
                          </span>
                          <span className="text-capitalize">{track.carrier}</span>
                        </div>
                      )}
                      {track.status && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">
                            Status {orderDetails.tracking!.length > 1 ? `#${index + 1}` : ''}
                          </span>
                          <span className="text-capitalize">{track.status.replace('_', ' ')}</span>
                        </div>
                      )}
                      {track.estimatedDelivery && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Estimated Delivery</span>
                          <span>{new Date(track.estimatedDelivery).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderDetail;
